import * as Sentry from "@sentry/react";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { useMemo } from "react";
import type { RouteObject } from "react-router-dom";
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  ScrollRestoration,
} from "react-router-dom";

import { Spinner } from "@/components/atoms/Spinner/Spinner";
import { ErrorPage } from "@/features/error";
import { protectedRoutes } from "./protected";
import { publicRoutes } from "./public";

const routes: (queryClient: QueryClient) => RouteObject[] = (
  queryClient: QueryClient
) => [...publicRoutes, ...protectedRoutes(queryClient)];

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const RouterRoot = () => {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
};

const createAppRouter = (queryClient: QueryClient) =>
  sentryCreateBrowserRouter([
    {
      element: <RouterRoot />,
      errorElement: <ErrorPage />,
      hydrateFallbackElement: (
        <output className="fixed inset-0 grid place-content-center bg-white">
          <Spinner className="size-5 text-primary" />
          <span className="sr-only">読み込み中…</span>
        </output>
      ),
      children: [...routes(queryClient)],
    },
  ]);

export const AppRoutes = () => {
  const queryClient = useQueryClient();

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  return (
    <NuqsAdapter
      processUrlSearchParams={(search) => {
        search.sort();
        return search;
      }}
    >
      <RouterProvider router={router} />
    </NuqsAdapter>
  );
};
